import { useMemo } from 'react';

import { keepPreviousData, useInfiniteQuery as useTanInfiniteQuery } from '@tanstack/react-query';
import { AGGREGATE_NAMES, CHAT_RESOLVERS } from 'cpool-constants';
import type { SchemaChatResolver_GetMessages } from 'cpool-tables/backend-resolvers';
import { schemaChatResolver_GetMessages } from 'cpool-tables/backend-resolvers';

import { DEFAULT_MESSAGE_PAGE_SIZE } from '~/features/whatsapp/constants';
import { getChatMessagesInfiniteQueryKey } from '~/features/whatsapp/utils/getChatMessagesInfiniteQueryKey';
import { buildQueryString, fetcher, useResolverAclGate } from '~/queryUtils/useQuery';

type MessagesPage = SchemaChatResolver_GetMessages;

export const useChatMessages = (chatId: string, limit = DEFAULT_MESSAGE_PAGE_SIZE) => {
    const aclGate = useResolverAclGate({}, AGGREGATE_NAMES.CHAT, CHAT_RESOLVERS.getMessages);
    const enabled = Boolean(chatId) && !!aclGate.allowed;
    const queryKey = getChatMessagesInfiniteQueryKey(chatId, limit);

    const result = useTanInfiniteQuery({
        enabled,
        getNextPageParam: (lastPage: MessagesPage) => {
            if (lastPage.length < limit) return undefined;
            return lastPage[0]?.sequence;
        },
        initialPageParam: undefined,
        placeholderData: keepPreviousData,
        queryFn: async ({ pageParam, signal }) => {
            const searchParams = {
                chatId,
                limit,
                ...(pageParam !== undefined ? { before: pageParam } : {}),
            };
            const queryUrl = `/query/${AGGREGATE_NAMES.CHAT}/${CHAT_RESOLVERS.getMessages}${buildQueryString(
                searchParams,
            )}`;

            return fetcher<typeof schemaChatResolver_GetMessages>({
                meta: { queryUrl, schema: schemaChatResolver_GetMessages },
                signal,
            });
        },
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey,
    });

    const messages = useMemo(() => [...(result.data?.pages ?? [])].reverse().flat(), [result.data?.pages]);

    return {
        ...result,
        isLoading: result.isLoading || aclGate.pending,
        messages,
    };
};
